/**
 * SubDelegateSignupPage — /sub-delegate-signup orchestrator (plan §6.2 / §4).
 * Reads the token, validates it, then branches on auth state. No form logic,
 * no styling — each branch is its own component.
 */

import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import {
  InviteExpiredError,
  InviteFormSkeleton,
  InviteSuccessState,
} from '@/shared/components/invite-signup';
import { trackRumAction } from '@/shared/observability/rum';
import {
  validateSubDelegateInvite,
  acceptSubDelegateInvite,
  acceptAdditionalSubDelegateInvite,
} from '../api/subDelegateInviteApi';
import type {
  SubDelegateInvitePublicResponse,
  SubDelegateInviteApiError,
} from '../types/subDelegateInvite';
import { userRoleLabel } from '../utils/labels';
import { myWorkspacesQueryKey } from '../context/WorkspaceProvider';
import { SubDelegateSignupForm } from './SubDelegateSignupForm';
import { AdditionalInviteConfirm } from './AdditionalInviteConfirm';
import { AlreadyLoggedInBlock } from './AlreadyLoggedInBlock';
import { EmailMismatchBlock } from './EmailMismatchBlock';
import { SubDelegateSuccessState } from './SubDelegateSuccessState';

const STASH_KEY = 'subDelegateInviteToken';
const emailsMatch = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

export const SubDelegateSignupPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const queryClient = useQueryClient();

  const token =
    searchParams.get('token') ?? sessionStorage.getItem(STASH_KEY) ?? '';

  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [additionalOwner, setAdditionalOwner] = useState<string | null>(null);

  const { data: invite, isLoading, error } = useQuery<
    SubDelegateInvitePublicResponse,
    SubDelegateInviteApiError
  >({
    queryKey: ['sub-delegate-invite', token],
    queryFn: () => validateSubDelegateInvite(token),
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () =>
      acceptSubDelegateInvite(token, {
        firstName: invite!.firstName,
        lastName: invite!.lastName,
        email: invite!.email,
        termsAccepted: true,
      }),
    onSuccess: (data) => {
      sessionStorage.removeItem(STASH_KEY);
      trackRumAction('workspace.sub_delegate.invite_accepted', {});
      setSuccessEmail(data.email);
      setTimeout(() => {
        navigate(`/?email=${encodeURIComponent(data.email)}&autoLogin=true`);
      }, 2000);
    },
  });

  const additionalMutation = useMutation({
    mutationFn: () => acceptAdditionalSubDelegateInvite(token),
    onSuccess: (data) => {
      sessionStorage.removeItem(STASH_KEY);
      trackRumAction('workspace.sub_delegate.invite_accepted', {
        additional: true,
      });
      // Refresh the delegate's workspace list so the WorkspaceBanner picks up
      // the newly added workspace and renders the switcher dropdown.
      queryClient.invalidateQueries({ queryKey: myWorkspacesQueryKey });
      setAdditionalOwner(data.ownerName);
    },
  });

  const signOutWithStash = () => {
    if (token) sessionStorage.setItem(STASH_KEY, token);
    logout();
  };

  if (successEmail) return <InviteSuccessState email={successEmail} />;
  if (additionalOwner)
    return <SubDelegateSuccessState ownerName={additionalOwner} />;

  if (!token) return <InviteExpiredError errorType="not_found" />;
  if (isLoading) return <InviteFormSkeleton />;
  if (error || !invite) {
    const errorType = error?.statusCode === 410 ? 'expired' : 'not_found';
    return <InviteExpiredError errorType={errorType} />;
  }

  const role = (user?.role as string | undefined) ?? null;
  const isSubDelegate = role === 'SUB_DELEGATE';

  if (isAuthenticated && user) {
    if (isSubDelegate) {
      if (emailsMatch(user.email, invite.email)) {
        return (
          <AdditionalInviteConfirm
            invite={invite}
            isSubmitting={additionalMutation.isPending}
            apiError={additionalMutation.error?.message}
            onAccept={() => additionalMutation.mutate()}
          />
        );
      }
      return (
        <EmailMismatchBlock
          inviteEmail={invite.email}
          currentUserEmail={user.email}
          ownerName={invite.ownerName}
          onSignOut={signOutWithStash}
        />
      );
    }

    return (
      <AlreadyLoggedInBlock
        currentUserName={[user.firstName, user.lastName]
          .filter(Boolean)
          .join(' ')}
        currentUserRoleLabel={userRoleLabel(role ?? undefined)}
        ownerName={invite.ownerName}
        onSignOut={signOutWithStash}
      />
    );
  }

  return (
    <SubDelegateSignupForm
      invite={invite}
      isSubmitting={acceptMutation.isPending}
      apiError={acceptMutation.error?.message}
      onSubmit={() => acceptMutation.mutate()}
    />
  );
};
